/**
 * The platform's RCS rate card: what every client is charged per billing event
 * unless it has its own prices, the minimum no client is ever charged under,
 * and the balance a send must leave in a wallet.
 *
 * Raising a minimum protects every client's own prices at once — they are
 * charged at the minimum without anybody editing them.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  PageHeader,
  QueryPending,
  ReadOnly,
  api,
  toast,
  useCan,
} from '@connect/web-shared';

import { PRICE, RATE_KEYS, belowMinimum, rcsKeys, type RcsOverview, type RcsPlatformPricing } from './rcs-wallet';

export function RcsPricingPage() {
  const overview = useQuery({ queryKey: rcsKeys.overview, queryFn: () => api.get<RcsOverview>('/messaging/rcs') });
  return (
    <div className="space-y-4">
      <PageHeader
        title="RCS pricing"
        description="What clients pay for RCS, per billing event and before GST. Messages are charged to each client's prepaid RCS wallet as they are delivered."
      />
      {overview.data === undefined ? (
        <QueryPending query={overview} label="Loading RCS pricing" />
      ) : (
        <Editor key={JSON.stringify(overview.data.pricing)} overview={overview.data} />
      )}
    </div>
  );
}

function Editor({ overview }: { overview: RcsOverview }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('messaging.rcs.edit');
  const [card, setCard] = useState<RcsPlatformPricing>(overview.pricing);
  const labels = Object.fromEntries(overview.rate_labels.map((l) => [l.key, l.label]));
  const under = belowMinimum(card.rates, card.minimums);
  const malformed =
    RATE_KEYS.some((k) => !PRICE.test(card.rates[k]) || !PRICE.test(card.minimums[k])) ||
    !/^\d{1,7}(\.\d{1,2})?$/.test(card.minimum_balance);
  const changed = JSON.stringify(card) !== JSON.stringify(overview.pricing);

  const save = useMutation({
    mutationFn: () => api.put<RcsOverview>('/messaging/rcs/pricing', card),
    onSuccess: (data) => {
      queryClient.setQueryData(rcsKeys.overview, data);
      toast.success('RCS prices saved', 'Charges from now on use them.');
    },
    onError: (e) => toast.error('Not saved', e instanceof ApiError ? e.message : undefined),
  });

  return (
    <ReadOnly when={!canEdit}>
      <Card>
        <CardHeader
          title="Rate card"
          description="A client with its own prices is still never charged under a minimum. Set each minimum to what the carriers charge Connect."
        />
        <CardBody className="space-y-4">
          <div className="overflow-hidden rounded-control border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-2 text-left text-xs text-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">Billing event</th>
                  <th className="w-40 px-3 py-2 font-medium">Price (₹)</th>
                  <th className="w-40 px-3 py-2 font-medium">Minimum (₹)</th>
                </tr>
              </thead>
              <tbody>
                {RATE_KEYS.map((key) => (
                  <tr key={key} className="border-t border-line align-top">
                    <td className="px-3 py-2 text-body">{labels[key] ?? key}</td>
                    <td className="px-3 py-1.5">
                      <Input
                        size="sm"
                        aria-label={`${labels[key]} price`}
                        inputMode="decimal"
                        value={card.rates[key]}
                        error={under.includes(key) ? 'Under its minimum' : undefined}
                        onChange={(e) => setCard({ ...card, rates: { ...card.rates, [key]: e.target.value.trim() } })}
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <Input
                        size="sm"
                        aria-label={`${labels[key]} minimum`}
                        inputMode="decimal"
                        value={card.minimums[key]}
                        onChange={(e) =>
                          setCard({ ...card, minimums: { ...card.minimums, [key]: e.target.value.trim() } })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Input
            label="Balance a send must leave (₹)"
            inputMode="decimal"
            containerClassName="max-w-xs"
            value={card.minimum_balance}
            hint="A send goes only when the wallet covers it, as if every message is delivered, plus this."
            onChange={(e) => setCard({ ...card, minimum_balance: e.target.value.trim() })}
          />
          {canEdit && changed ? (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setCard(overview.pricing)}>
                Discard
              </Button>
              <Button loading={save.isPending} disabled={under.length > 0 || malformed} onClick={() => save.mutate()}>
                Save prices
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </ReadOnly>
  );
}
